import { useState } from 'react'
import { UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/Input'
import { useGuestSuggestions } from '@/lib/queries/useGuestSuggestions'
import { useDebounce } from '@/hooks/useDebounce'
import type { GuestSuggestion } from '@/lib/queries/useGuestSuggestions'
import type { UseFormRegister, FieldErrors, UseFormSetValue } from 'react-hook-form'

interface GuestFieldsProps {
  register: UseFormRegister<any>
  setValue: UseFormSetValue<any>
  errors: FieldErrors<any>
}

export function GuestFields({ register, setValue, errors }: GuestFieldsProps) {
  const { t } = useTranslation()

  // Local query state — updated on every keystroke, drives the search
  const [nameQuery,  setNameQuery]  = useState('')
  const [emailQuery, setEmailQuery] = useState('')
  const [focused, setFocused] = useState<'name' | 'email' | null>(null)

  const debouncedName  = useDebounce(nameQuery,  300)
  const debouncedEmail = useDebounce(emailQuery, 300)

  const searchTerm   = focused === 'name' ? debouncedName : focused === 'email' ? debouncedEmail : ''
  const showDropdown = focused !== null && searchTerm.length >= 2

  const { data: suggestions = [] } = useGuestSuggestions(showDropdown ? searchTerm : undefined)

  const handleSelect = (g: GuestSuggestion) => {
    setValue('guest.name',  g.name,  { shouldValidate: true })
    setValue('guest.email', g.email, { shouldValidate: true })
    setValue('guest.phone', g.phone)
    setNameQuery(g.name)
    setEmailQuery(g.email)
    setFocused(null)
  }

  const nameReg  = register('guest.name')
  const emailReg = register('guest.email')

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-[var(--color-text-secondary)]">
        {t('guest.title', 'Guest Information')}
      </h4>

      {/* Name field */}
      <div>
        <Input
          label={t('guest.name')}
          placeholder={t('guest.name')}
          error={(errors?.guest as any)?.name?.message ? t('guest.nameRequired') : undefined}
          autoComplete="off"
          {...nameReg}
          onChange={(e) => { nameReg.onChange(e); setNameQuery(e.target.value) }}
          onFocus={() => setFocused('name')}
          onBlur={(e) => { nameReg.onBlur(e); setTimeout(() => setFocused((f) => f === 'name' ? null : f), 200) }}
        />
        {showDropdown && focused === 'name' && (
          <SuggestionList
            suggestions={suggestions}
            addNewLabel={nameQuery}
            onSelect={handleSelect}
            onAddNew={() => setFocused(null)}
          />
        )}
      </div>

      {/* Email + Phone */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Input
            label={t('guest.email')}
            type="email"
            placeholder="email@example.com"
            error={(errors?.guest as any)?.email?.message as string}
            autoComplete="off"
            {...emailReg}
            onChange={(e) => { emailReg.onChange(e); setEmailQuery(e.target.value) }}
            onFocus={() => setFocused('email')}
            onBlur={(e) => { emailReg.onBlur(e); setTimeout(() => setFocused((f) => f === 'email' ? null : f), 200) }}
          />
          {showDropdown && focused === 'email' && (
            <SuggestionList
              suggestions={suggestions}
              addNewLabel={emailQuery}
              onSelect={handleSelect}
              onAddNew={() => setFocused(null)}
            />
          )}
        </div>
        <Input
          label={t('guest.phone')}
          placeholder="+1 234 567 890"
          {...register('guest.phone')}
        />
      </div>

      {/* Notification checkboxes */}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] cursor-pointer">
          <input type="checkbox" className="rounded accent-[var(--color-accent-amber)]" {...register('guest.notificationSms')} />
          {t('guest.notifyBySms')}
        </label>
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] cursor-pointer">
          <input type="checkbox" className="rounded accent-[var(--color-accent-amber)]" {...register('guest.notificationEmail')} />
          {t('guest.notifyByEmail')}
        </label>
      </div>
    </div>
  )
}

interface SuggestionListProps {
  suggestions: GuestSuggestion[]
  addNewLabel: string
  onSelect: (g: GuestSuggestion) => void
  onAddNew: () => void
}

function SuggestionList({ suggestions, addNewLabel, onSelect, onAddNew }: SuggestionListProps) {
  return (
    <div className="mt-1 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] shadow-lg overflow-hidden">
      {suggestions.slice(0, 6).map((g, i) => (
        <button
          key={i}
          type="button"
          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[var(--color-bg-elevated)] text-left transition-colors"
          onMouseDown={(e) => { e.preventDefault(); onSelect(g) }}
        >
          <div className="h-7 w-7 rounded-full bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] flex items-center justify-center text-xs font-semibold text-[var(--color-text-muted)] shrink-0">
            {(g.name || '?').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">{g.name}</p>
            {g.email && <p className="text-xs text-[var(--color-text-muted)] truncate">{g.email}</p>}
            {g.phone && !g.email && <p className="text-xs text-[var(--color-text-muted)] truncate">{g.phone}</p>}
          </div>
        </button>
      ))}

      {suggestions.length > 0 && <div className="border-t border-[var(--color-border-subtle)]" />}

      <button
        type="button"
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[var(--color-bg-elevated)] text-left transition-colors"
        onMouseDown={(e) => { e.preventDefault(); onAddNew() }}
      >
        <div className="h-7 w-7 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
          <UserPlus className="h-3.5 w-3.5 text-blue-400" />
        </div>
        <p className="text-sm text-[var(--color-text-muted)]">
          Add <span className="font-medium text-[var(--color-text-primary)]">"{addNewLabel}"</span> as a new customer
        </p>
      </button>
    </div>
  )
}
